import angular from "angular";

import { Ajax, Translations, WorktimeApiConnector } from "@salaxy/core";
import type { calcReportType, CalculationListItem, PayrollDetails, WorktimeImport, WorktimeImportInStagingListItem, WorktimeImportPreviewMetadata, WorktimeImportRow } from "@salaxy/core";
import { ReportsService, SessionService, UiHelpers, UserSettingsService } from "../../../services";

/** Controller that connects to the Import Staging area */
export class ImportStagingController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["AjaxNg1", "$timeout", "UserSettingsService", "$sce", "SessionService", "UiHelpers", "ReportsService"];

  /**
   * If provided by the parent (payroll controller), contains the list off calculations commit into the Payroll
   * TODO: Consider including this to WorktimeImport wihtin the commit stage (also some other meta data)?
   */
  public payrollCalcs: CalculationListItem[] = [];

  /** API methods for the Worktime Service. */
  public worktimeApi: WorktimeApiConnector;

  /** Itsems to show in the salary slips list. */
  public salarySlipListType: "preview" | "committed" | "all" | "distinct" = "all";

  /** The payroll that is potentially set by the parent Payroll component. This is only used in the init, changing it later will not take any effect. */
  public payroll: PayrollDetails | undefined = undefined;

  public debugJson: string = "";

  private _payrollId: string | undefined = undefined;

  private salarySlipCache = {
    /** Employment ID for which the data has been fetched. */
    personalId: undefined as string | undefined,
    /** List of calculations for the employment ID */
    calcList: undefined as CalculationListItem[] | undefined,
    /** Type of the list (filtering of the items on server-side) */
    type: undefined as string | undefined,
  };

  constructor(
    private ajax: Ajax,
    private $timeout: angular.ITimeoutService,
    private userSettingsService: UserSettingsService,
    private $sce: angular.ISCEService,
    private sessionService: SessionService,
    private uiHelpers: UiHelpers,
    private reportsService: ReportsService,
  ) {
  }

  $onInit(): void {
    if (!this.isSupported) {
      return;
    }
    this.worktimeApi = new WorktimeApiConnector(this.serverUrl + "/api/v01/worktime", this.ajax);
    this.worktimeApi.reloadList().then(() => {
      this.$timeout();
    });
    if (this.payroll) {
      this.payrollId = this.payroll.id;
    }
    this.worktimeApi.fetchAllPayrolls().then(() => {
      this.$timeout();
    });
  }

  /** Gets or sets the current payroll in the controller and also updates the selected payroll in the WorktimeApiConnector. */
  public get payrollId(): string | undefined {
    return this._payrollId;
  }
  public set payrollId(value: string | undefined) {
    this._payrollId = value;
    this.worktimeApi.detailsPayrollId = value;
  }

  /**
   * If supported for the company or partner, this URL is the integration server used in for the imports.
   */
  public get serverUrl(): string | undefined {
    return this.sessionService.session.settings.integration.serverUrl || undefined;
  }

  /**
   * Gets the user-specific setting (service), stored in the local storage.
   */
  public get userSettings() {
    return this.userSettingsService;
  }

  /** If true, the staginc server is supported for the partner. */
  public get isSupported(): boolean {
    return !!this.serverUrl;
  }

  /** Gets the calculations / salary slips for the employment. */
  public get currentEmploymentCalcs(): CalculationListItem[] {
    if (!this.worktimeApi.current?.preview?.employments?.length) {
      return [];
    }
    const personalId = this.worktimeApi.current.preview.employments[0].identity.personalId;
    if (this.salarySlipCache.personalId !== personalId || this.salarySlipCache.type !== this.salarySlipListType) {
      this.salarySlipCache.personalId = personalId;
      this.salarySlipCache.calcList = undefined;
      this.salarySlipCache.type = this.salarySlipListType;
      this.ajax.getJSON(this.serverUrl + `/api/v01/worktime/employment/${personalId}/calculations?type=${this.salarySlipListType}`)
        .then((result: any) => {
          this.salarySlipCache.calcList = result.value;
        });
    }
    return this.salarySlipCache.calcList;
  }

  /**
   * Commits the current payroll.
   * @param scope - The scope of the commit: Payroll commits the entire payroll in the controller.
   * Employment commits the selected employment only and the Payroll specified in the employment.
   */
  public async commitPayroll(scope: "payroll" | "employment") {
    const commitSingle = async (payrollId: string, employmentId: string) => {
      await this.ajax.postJSON(this.serverUrl + `/api/v01/worktime/commit-payroll/${payrollId}/${employmentId}`, undefined);
    };

    if (scope == "payroll") {
      if (!this.payrollId) {
        throw new Error("No payroll selected.");
      }
      const list = this.worktimeApi.getFilteredList(this.payrollId);
      const columns = [
        { title: Translations.get("SALAXY.UI_Terms.name"), field: "sortableName" },
        { title: Translations.get("SALAXY.UI_Terms.personalId"), field: "personalId" },
      ];
      await this.uiHelpers.showBatchProcessDialog(Translations.get("SALAXY.NG1.ImportStagingComponent.dialog.commitPayroll"), list, async (item, _data) => {
        if (Array.isArray(item)) {
          throw new Error("Batch processing not supported for payroll commit.");
        }
        await commitSingle(this.payrollId, item.id);
        return [{
          message: "",
          result: undefined,
        }];
      }, undefined, {}, columns, undefined, true);
      this.$timeout(() => {
        this.reloadUi();
      });
      return;
    }

    const loader = this.uiHelpers.showLoading(Translations.get("SALAXY.NG1.ImportStagingComponent.dialog.commitPayroll"));
    await commitSingle(this.worktimeApi.detailsPayrollId, this.worktimeApi.current.id);
    loader.dismiss();
    this.reloadUi();
  }

  /** Reloads the current user interface: Especially the payroll if we are in the Payroll view. */
  public reloadUi() {
    this.sessionService.refreshUi();
  }

  /**
   * Gets the totals for the selected payroll or the entire history if no payroll is selected.
   * @param row - The row for which to get the totals.
   */
  public getTotals(row: WorktimeImportInStagingListItem | "all"): WorktimeImportPreviewMetadata {
    if (row == "all") {
      let rows: WorktimeImportPreviewMetadata[] = [];
      if (this.worktimeApi?.list) {
        rows = this.payrollId
          ? this.worktimeApi.list.map((item) => item.payrolls[this.payrollId]).filter((item) => item)
          : this.worktimeApi.list.map((item) => item.totals);
      }
      return this.uiHelpers.cache(this, "allTotals", () => {
        return rows.reduce((acc: WorktimeImportPreviewMetadata, cur: WorktimeImportPreviewMetadata) => {
          return {
            calcs: {
              count: acc.calcs.count + cur.calcs.count,
            },
            period: cur.period, // Add min/max if necessary
            rows: {
              committed: acc.rows.committed + cur.rows.committed,
              count: acc.rows.count + cur.rows.count,
              deleted: acc.rows.deleted + cur.rows.deleted,
              edited: acc.rows.edited + cur.rows.edited,
              error: acc.rows.error + cur.rows.error,
              ignored: acc.rows.ignored + cur.rows.ignored,
              new: acc.rows.new + cur.rows.new,
              preview: acc.rows.preview + cur.rows.preview,
            },
            salaryDate: cur.salaryDate, // Add min/max if necessary
          };
        }, {
          calcs: {
            count: 0,
          },
          rows: {
            committed: 0,
            count: 0,
            deleted: 0,
            edited: 0,
            error: 0,
            ignored: 0,
            new: 0,
            preview: 0,
          }
        } as any);
      }, /** Add upadated at to WorktimeImportPreviewMetadata for quicker cache  */);
    }
    if (this.payrollId) {
      const totals = row.payrolls[this.payrollId];
      if (this.payrollCalcs && totals && !(totals as any).committed) {
        const committedCalc = this.payrollCalcs.find((calc) => calc.data.employmentId == row.id);
        if (committedCalc) {
          (totals as any).committed = {
            gross: committedCalc.grossSalary,
            net: committedCalc.data.workerPayment,
          };
        }
      }
      return totals;
    }
    return row.totals;
  }

  /**
   * Recalculates the current worktime (if selected).
   */
  public async recalculateWorktime() {
    const id = this.worktimeApi.current?.id;
    this.showDetails(undefined);
    const loader = this.uiHelpers.showLoading(Translations.get("SALAXY.NG1.ImportStagingComponent.dialog.recalculating"));
    await this.worktimeApi.recalculateWorktime(id);
    this.showDetails(id);
    loader.dismiss();
  }

  /**
   * Saves the manual rows to the server and refreshes the view.
   * @returns The updated worktime import.
   */
  public async saveManualRows(): Promise<void> {
    await this.worktimeApi.saveManualRows();
    await this.recalculateWorktime();
  }

  /** Deletes the current worktime (with are you sure) */
  public async deleteWorktime() {
    if (!this.worktimeApi.current) {
      return;
    }
    this.uiHelpers.showConfirm("SALAXY.NG1.ApiCrudObjectComponent.deleteDialog").then((result: boolean) => {
      if (result) {
        const id = this.worktimeApi.current.id;
        this.showDetails(undefined);
        this.worktimeApi.delete(id);
      }
    });
  }

  /**
   * Recalculates all the worktimes in the list.
   */
  public async recalculateAllWorktimes() {
    const loader = this.uiHelpers.showLoading(Translations.get("SALAXY.NG1.ImportStagingComponent.dialog.recalculating"));
    const list = this.worktimeApi.getFilteredList(this.payrollId);
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      loader.setText(`Recalculating ${item.sortableName} ${i + 1}/${list.length}...`);
      await this.worktimeApi.recalculateWorktime(item.id);
    }
    loader.dismiss();
  }

  /**
   * Shows a report for the current calculation using a modal dialog.
   * @param calculationId - Identifier of the calculation to show.
   * @param reportType - Type of report to show
   */
  public showReportDialog(calculationId: string, reportType: calcReportType = "salarySlip") {
    // TODO: Add support for all report types view
    const isPreview = calculationId.startsWith("preview-");
    if (isPreview) {
      const calc = this.worktimeApi.current.preview.calculations.find((calc) => calc.id == calculationId);
      if (calc) {
        this.reportsService.showReportModalForCalc(reportType, calc, "page1", true);
        return;
      }
      throw new Error(`Calculation ${calculationId} not found in preview.`);
    }
    const url = `${this.serverUrl}/api/v01/worktime/calculations/${calculationId}`;
    this.ajax.getJSON(url).then((calc) => {
      this.reportsService.showReportModalForCalc(reportType, calc, "page1", false);
    });
  }

  /**
   * Shows the salary slip PDF in the IFrame UI.
   * Sets the currentEmploymentPdf to the PDF of the salary slip (Base64 resource URI).
   * @param calculationId Identifier of the calculation for which the PDF is shown.
   */
  public showSalarySlipPdf(calculationId: string | undefined) {
    this.currentEmploymentPdf = "loading";
    if (!calculationId) {
      this.currentEmploymentPdf = undefined;
      return;
    }
    this.ajax.getHTML(this.serverUrl + `/api/v01/worktime/calculations/${calculationId}/pdf`).then((pdf) => {
      this.currentEmploymentPdf = this.$sce.trustAsResourceUrl(`data:application/pdf;base64, ${encodeURI(pdf)}`);
      this.$timeout();
    });
  }

  /**
   * Shows the salary slip HTML in the IFrame UI.
   * @param calculationId Identifier of the calculation for which the HTML is shown.
   */
  public showSalarySlipHtml(calculationId: string | undefined): void {
    this.currentEmploymentPdf = this.$sce.trustAsResourceUrl(this.serverUrl + `/api/v01/worktime/calculations/${calculationId}/html?access_token=` + this.ajax.getCurrentToken());
  }

  /**
   * Shows (hides) an item in the details view
   * @param id - The ID of the item to show. If undefined, hides the details view.
   */
  public showDetails(id: string | undefined) {
    if (id) {
      this.worktimeApi.showDetails(id);
    } else {
      this.worktimeApi.setCurrent(undefined);
    }
  }

  /** Returns true if the offcanvas (dialog) should be shown. */
  public get showOffcanvas(): boolean {
    return !!this.worktimeApi.current;
  }

  /** PDF salary slip that was last fetched for the current employment */
  public currentEmploymentPdf: string | undefined = undefined;

  /**
   * Uploads a JSON file for testing purposes.
   * Should be added to a form submit event.
   * @param event - The change event that triggered by the file upload
   * ...or string "clipboard" for clipboard data.
   * @example
   * ```html
   * <form ng-submit="$ctrl.uploadTestFile($event)">
   *   <input type="file" class="form-control" />
   *   <button type="submit" class="btn btn-default">Upload</button>
   * </form>
   * ```
   */
  public async uploadTestFile(event?: Event | "clipboard") {
    let data: WorktimeImport;
    if (event == "clipboard") {
      const json = await window.navigator.clipboard.readText();
      data = JSON.parse(json);
    } else {
      const form = event?.target as HTMLFormElement;
      if (!form.tagName || form.tagName.toLowerCase() !== "form") {
        throw new Error(`Method uploadTestFile should be connected to submit event of a form, e.g. <form ng-submit="$ctrl.uploadTestFile($event)">.`);
      }
      const ipt = form.querySelector("input[type=file]") as HTMLInputElement;
      if (!ipt) {
        throw new Error("No file input found in the form.");
      }
      const file = ipt.files?.[0];
      if (!file) {
        return;
      }
      const text = await file.text();
      data = JSON.parse(text);
    }
    if (!data.rows && !data.employments) {
      throw new Error("Invalid data: Rows or employments are required.");
    }
    let result: WorktimeImport;
    try {
      result = await this.worktimeApi.callUpdateWorktime(data);
    } catch (error) {
      this.uploadResults = error;
      this.$timeout();
      throw error;
    }

    const validationErrors = (result.rows ?? []).concat(result.employments ?? [])
      .filter((row) => !row.validation.isValid);
    if (validationErrors.length) {
      console.error("Validation errors", validationErrors);
      this.uploadResults = validationErrors;
      alert("Validation errors found, see console for details.");
    } else {
      console.debug("Result", result);
      this.uploadResults = result;
      alert("Updates have been saved and validated.");
    }
    this.$timeout();
  }

  /**
   * Uploads a JSON string for testing purposes.
   * @param text - The JSON string to upload
   */
  public async uploadTestJson(text: string) {
    const data = JSON.parse(text);
    let result: WorktimeImport;
    try {
      result = await this.worktimeApi.callUpdateWorktime(data);
    } catch (error) {
      this.uploadResults = error;
      this.$timeout();
      throw error;
    }

    const validationErrors = (result.rows ?? []).concat(result.employments ?? [])
      .filter((row) => !row.validation.isValid);
    if (validationErrors.length) {
      console.error("Validation errors", validationErrors);
      this.uploadResults = validationErrors;
      alert("Validation errors found, see console for details.");
    } else {
      console.debug("Result", result);
      this.uploadResults = result;
      alert("Updates have been saved and validated.");
    }
    this.$timeout();
  }

  /**
   * Formats the debug JSON string.
   */
  public formatDebugJson() {
    this.debugJson = JSON.stringify(JSON.parse(this.debugJson), null, 2);
  }

  /**
   * Adds a blank WorktimeImport object to the debug JSON editor.
   * Uses WorktimeApiConnector.getBlank() to generate the empty object.
   */
  public debugJsonAddBlank() {
    this.debugJson = JSON.stringify(WorktimeApiConnector.getBlank(), null, 2);
  }

  public uploadResults: any = undefined;

  /**
   * Marks the selected rows with given state.
   * @param rows - The rows to mark.
   * @param status - The status to set.
   * Status "new" is essentially reverting the state from "ignored" => final state may be "preview", "new" or "error" depending on other properties of the row.
   */
  public setStatus(rows: WorktimeImportRow[], status: "ignored" | "new") {
    const loader = this.uiHelpers.showLoading();
    this.worktimeApi.setStatus(rows, status).then((_result: WorktimeImport) => {
      loader.dismiss();
      this.$timeout();
    });
  }

  /**
   * Deletes the selected rows from the current worktime import and saves the changes .
   * @param rows - The rows to delete.
   */
  public async deleteRows(rows: WorktimeImportRow[]) {
    const confirm = await this.uiHelpers.showConfirm("Haluatko varmaan poistaa valitut rivit?", "Rivit poistetaan työajan tuonnista, mutta ne saatetaan tuoda uudelleen myöhemmin, jos lähdejärjestelmän dataa päivitetään.");
    if (!confirm) {
      return;
    }
    const loader = this.uiHelpers.showLoading();
    this.worktimeApi.current.rows = this.worktimeApi.current.rows.filter((row) => !rows.includes(row));
    await this.worktimeApi.saveCurrent();
    loader.dismiss();
  }

  /**
   * Moves the selected rows to the given payroll.
   * @param rows - The rows to move.
   * @param payrollId - The payroll id to move to.
   * @returns The updated worktime import.
   */
  public async moveToPayroll(rows: WorktimeImportRow[], payrollId: string) {
    const loader = this.uiHelpers.showLoading();
    this.worktimeApi.moveToPayroll(rows, payrollId).then((_result: WorktimeImport) => {
      loader.dismiss();
      this.$timeout();
    });
  }

  /** The current status group. */
  public get statusGroup() {
    return this.statusGroups.find((x) => x.active) || this.statusGroups[0];
  }
  public set statusGroup(value: {
    key: string;
    statuses: string[];
    text: string;
    count: number;
    active?: boolean;
  }) {
    this.statusGroups.forEach((x) => x.active = x.key == value.key);
  }

  private updateCounts() {
    // TODO: Currently diabled, need to figure out how to reliably update counts.
    this.statusGroups.forEach((x) => x.count = this.worktimeApi.filteredRows.filter((row) => x.statuses.includes(row.status)).length);
  }

  /** The status groups availab. */
  public statusGroups: {
    key: string;
    statuses: string[];
    text: string;
    count: number;
    active?: boolean;
  }[]
    = [
      {
        key: "todo",
        statuses: ["preview", "new", "error", "deleted", "edited"],
        text: "SALAXY.NG1.ImportStagingComponent.statusGroup.todo",
        count: 0,
        active: true,
      },
      {
        key: "waitingCommit",
        statuses: ["preview", "new", "edited"],
        text: "SALAXY.NG1.ImportStagingComponent.statusGroup.waitingCommit",
        count: 0,
      },
      {
        key: "committed",
        statuses: ["committed"],
        text: "SALAXY.NG1.ImportStagingComponent.status.committed",
        count: 0,
      },
      {
        key: "all",
        statuses: [],
        text: "SALAXY.NG1.ImportStagingComponent.status.all",
        count: 0,
      },
      {
        key: "ignored",
        statuses: ["ignored"],
        text: "SALAXY.NG1.ImportStagingComponent.status.ignored",
        count: 0,
      },
      {
        key: "error",
        statuses: ["error"],
        text: "SALAXY.NG1.ImportStagingComponent.status.error",
        count: 0,
      },
      {
        key: "preview",
        statuses: ["preview"],
        text: "SALAXY.NG1.ImportStagingComponent.status.preview",
        count: 0,
      },
    ];
}